import { BodyCell, DataGrid, HeaderColumn } from '@tableau/widgets-datagrid';
import * as React from 'react';
import * as _ from 'underscore';

import { appStore } from '../../../../app/AppStore';
import { selectContentSort } from '../../../../app/selectors/ContentSortSelectors';
import { IOrderClause } from '../../../../ts/ServerApi/types/IOrderClause';
import { UserAgentSupport } from '../../../utilities/UserAgentSupport';
import { CommonStyles } from '../../CommonStyles';
import { QueryString } from '../../QueryString';
import { SorterActions } from '../../SorterActions';
import { DeprecatedReactMenu } from '../Menu';
import { SortableHeader } from './SortableHeader';

export interface SelectableSortKeyColumnOptions {
  columnName: string;
  items: SelectableSortKeyColumnItem[];
  defaultDataKey?: string;
  preferredWidth?: number;
}

export interface SelectableSortKeyColumnItem {
  dataKey: string;
  headerText: string;
  cellFormatter?: React.Factory<BodyCell.Props>;
}

export interface SelectableSortKeyColumnContext {
  queryString: QueryString;
  sorterActions: SorterActions;
}

/**
 * Instantiates a column with a selector menu in the header, which allows the
 * user to select different fields to display. Each field can have its own cell formatter.
 *
 * The initially-passed-in "text" in the column spec has no effect; it will be immediately overwritten
 * by the value in headerTextByKey for the currently-selected field.
 *
 * @returns {Utilities.Column}
 * @constructor
 */
export function SelectableSortKeyDataGridColumn(options: SelectableSortKeyColumnOptions,
                                                ctx: SelectableSortKeyColumnContext): DataGrid.Column {

  const items = options.items;
  const defaultDataKey = getDefaultDataKey(options.defaultDataKey, items);
  updateQueryStringIfNecessary(options.columnName, ctx, items, defaultDataKey);
  const dataKeyAndDirection = getDataKeyAndDirection(options.columnName, ctx, items);
  const dataKey = dataKeyAndDirection.dataKey
      || defaultDataKey
      || items[0].dataKey;

  const currentItem: SelectableSortKeyColumnItem = itemForDataKey(dataKey, items);

  return {
    name: options.columnName,
    dataKey: dataKey,
    direction: dataKeyAndDirection.direction || 'desc',
    headerText: currentItem.headerText,
    headerFormatter: headerFormatter(items, ctx),
    cellFormatter: currentItem.cellFormatter,
    preferredWidth: options.preferredWidth
  };
}

function getDefaultDataKey(dataKey: string, items: ArrayLike<SelectableSortKeyColumnItem>): string {
  return itemForDataKey(dataKey, items) && dataKey;
}

function itemForDataKey(dataKey: string, items: ArrayLike<SelectableSortKeyColumnItem>): SelectableSortKeyColumnItem {
  if (!dataKey) return null;
  return _.find(items, item => item.dataKey === dataKey);
}

function updateQueryStringIfNecessary(colName: string, ctx: SelectableSortKeyColumnContext,
                                      items: SelectableSortKeyColumnItem[], defaultDataKey: string) {

  const order = selectContentSort(appStore.getState());
  const relevantOrderClause = findRelevantOrderClause(order, items);

  if (relevantOrderClause) {
    if (relevantOrderClause.field === defaultDataKey) {
      // prevent the default datakey from appearing in the url
      ctx.queryString(colName, null);
    } else {
      ctx.queryString(colName, relevantOrderClause.field);
    }
  }
}

interface DataKeyAndDirection { dataKey: string; direction?: string; }

function getDataKeyAndDirection(
    colName: string, ctx: SelectableSortKeyColumnContext,
    items: SelectableSortKeyColumnItem[]): DataKeyAndDirection {

  let dataKey: string = null;
  let direction: string = null;

  const queryString = ctx.queryString();
  const order = selectContentSort(appStore.getState());
  const firstOrderClause = order && order[0];
  const relevantOrderClause = findRelevantOrderClause(order, items);

  // If the relevant clause is the primary sort clause, set the direction of the column to that sort order.
  if (relevantOrderClause && firstOrderClause === relevantOrderClause) {
    direction = relevantOrderClause.ascending ? 'asc' : 'desc';
  }

  if (queryString && queryString[colName] && itemForDataKey(queryString[colName], items)) {
    // If there is a query string and there is a valid data key in the query string, use that key.
    dataKey = queryString[colName];
  } else if (relevantOrderClause) {
    // Otherwise, if there is a relevant order clause, use the key from that clause.
    dataKey = relevantOrderClause.field;
  }

  return { dataKey, direction };
}

/**
 * Search the order store for an order clause that matches one of the possible data keys for this column.
 *
 * @returns {IOrderClause} first relevant order clause if one exists, else `undefined`
 */
function findRelevantOrderClause(order: ReadonlyArray<IOrderClause>, items: ReadonlyArray<SelectableSortKeyColumnItem>) {
  return _.find(order, (orderClause: IOrderClause) => itemForDataKey(orderClause.field, items));
}

type DropDownMenuButtonProps = DeprecatedReactMenu.ButtonProps & {
  readonly visible: boolean;
};

class DropDownMenuButton extends React.Component<DropDownMenuButtonProps> {
  public render(): JSX.Element {
    return React.createElement('span', {
      className: 'tb-drop-down-icon',
      style: {
        visibility: this.props.visible ? 'visible' : 'hidden',
      }
    });
  }
}

function headerFormatter(
    items: SelectableSortKeyColumnItem[],
    ctx: SelectableSortKeyColumnContext): React.Factory<any> {
  return React.createFactory(class extends React.Component<HeaderColumn.HeaderFormatterProps, any> {

    public displayName = 'SelectableSortKeyColumnHeaderFormatter';

    state = {
      menuHovered: false
    };

    render() {
      const menuButtonTop = (CommonStyles.Sizes.DataGridHeaderHeight - CommonStyles.Sizes.NormalIconSize) / 2;

      const menu = DeprecatedReactMenu.element({
        ref: 'menu',
        style: {
          fontWeight: 'normal',
          lineHeight: 'normal',
          fontSize: CommonStyles.Sizes.NormalFontSize,

          // vertically center the icon on the right side of the column header
          position: 'absolute',
          top: menuButtonTop,
          right: CommonStyles.Sizes.DefaultSpace
        },
        menuButtonFactory: (buttonProps: DeprecatedReactMenu.ButtonProps) =>
          React.createElement(DropDownMenuButton, {
            visible: buttonProps.menuOpen || this.props.hovered || UserAgentSupport.isTouch()
          }),

        // TODO: refactor this into a common component. Similar code in ActionsMenuColumnCell.ts
        menuPanelFactory: () => {
          const headerColProps: HeaderColumn.Props = this.props;
          const currentKey = headerColProps.col.dataKey;
          const menuItems =
            _.map(items, (item: SelectableSortKeyColumnItem) => {
              const key = item.dataKey;

              const isSelected = key === currentKey;
              const menuItemContent =
                [
                  React.createElement('span', {
                    key: key + '-dot',
                    className: 'tb-padded-right',
                    style: {
                      visibility: isSelected ? 'visible' : 'hidden'
                    }
                  }, '•'),
                  React.createElement('span', { key: key + '-text'}, item.headerText)
                ];

              return React.createElement('div', {
                key: key + '-container',
                className: 'tb-popup-menu-item',
                'data-tb-test-id': key,
                onClick: () => {
                  this.selectKey(key);
                  (<DeprecatedReactMenu>this.refs['menu']).closeMenu();
                }
              } as React.HTMLAttributes<HTMLDivElement>,
              menuItemContent);
            });

          return React.createElement('div', {
            className: 'tb-popup-menu-content tb-padded-small'
          }, menuItems);
        },
        menuPanelStyle: {
          // position drop down 3px below header cell
          top: menuButtonTop + CommonStyles.Sizes.NormalIconSize + CommonStyles.Sizes.DropdownOffset,
          right: -(CommonStyles.Sizes.DefaultSpace),
          left: 'auto'
        }
      });

      const innerHCellStyle = _.extend({}, CommonStyles.Layout.TextEllipsis, {
        marginRight: CommonStyles.Sizes.NormalIconSize + CommonStyles.Sizes.DefaultSpace
      });

      const outerHCellStyle = {
        textDecoration: (this.props.hovered && !this.state.menuHovered) ? 'underline' : 'none'
      };

      return React.createElement('div', {
          style: outerHCellStyle
        },
        React.createElement('div', {
            style: innerHCellStyle
          },
          SortableHeader.ArrowIcon({sorted:this.props.sorted, ascending:this.props.sortAscending}),
          (<HeaderColumn.Props>this.props).col.headerText
        ),
        React.createElement('div', {
          // stop propagation to prevent header cell from sorting.
          onClick: (event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation(),
          onMouseOver: () => this.setState({ menuHovered: true }),
          onMouseOut: () => this.setState({ menuHovered: false })
        }, menu)
      );
    }

    selectKey(key: string): void {
      const props: HeaderColumn.Props = this.props;
      if (props.col.dataKey === key) {
        // if the user selects the same metric in the menu, don't do anything
        return;
      }
      ctx.sorterActions.replaceOrderClause(props.col.dataKey, key, props.col.direction);
    }

  });
}
